<template lang="pug">
#send-family-safety-confirm-tab
  .register-body.mt-4
    //- 宛先種別
    InputTargetTypeForFamily(v-if="enableTarget" v-model="targetType")
    FormLabel(v-else v-model="_targetTypeLabel" key-name="宛先種別")
    //- 宛先種別：グループ 宛先選択モーダル表示ボタン
    TargetSelectButton(v-if="targetType == 'group' && enableTarget" v-model="groups" :isBusy="isBusy" @onTargetBtn="onTargetBtn")

    FormTextarea(
      key-name="メッセージ内容"
      name="text"
      v-model="openingSentence"
      placeholder="家族に送るメッセージ内容を入力してください。\n\n例：\n安否情報提供のテストを実施します。\nご覧になったら「確認しました」を押してください。"
      :validation-rules="{ max: 300, required: true  }"
      :maxlength="300"
      :styles="{ maxWidth: '760px', width: '760px', minHeight: '160px' }" required)

    FormLabel(key-name="情報提供項目：利用者個別状況" :value="residentStatuses")

    FormLabel(key-name="情報提供項目：施設全体状況" :value="facilityStatuses")

  .box-footer.register-footer
    ValidationAlert(v-model="showValidationAlert")
    .buttons.m-0
      button.btn.sned-message-btn(@click="onSendMessage(safetyConfirmsId)" type="button") 送信
      button.btn.cancel-btn(@click="onCancel" type="button") キャンセル

  GroupSelectModal(ref="groupSelectModal" :groupType.sync="groupType" @add="addSendGroupTarget")

  RelativeLoading(:isBusy="isBusy" :message="loadingMessage")
  PageLoading(:isBusy="isPageBusy" :message="pageLoadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import SystemError from "@/commons/models/SystemError";

import FamilySafetyConfirmsTemplatesService, { FamilySafetyConfirmsTemplatesGetModel } from "@/commons/services/FamilySafetyConfirmsTemplates";
import FamilySafetyConfirmMastersService, { FamilySafetyConfirmMastersGetModel, FamilySafetyConfirmMasterType } from "@/commons/services/FamilySafetyConfirmMasters";
import SendMessageGroupsService, { GroupType, SendMessageGroupsGetModel } from "@/commons/services/SendMessageGroups";
import { FamilySendSafetyConfirmLineMessagesPostModel, FamilySendSafetyConfirmMailMessagesPostModel, SafetyConfirmTarget } from "@/commons/services/SendSafetyConfirmMessages";
import SendMessageGroupMembersService, { SendMessageGroupMembersFamilyGetModel } from "@/commons/services/SendMessageGroupMembers";
import { SenderType } from "@/commons/models/Resident";
import FamiliesService, { FamilyGetModel } from "@/commons/services/Families";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import SafetyConfirmsService, { SafetyConfirmsGetModel } from "@/commons/services/SafetyConfirms";

import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import TargetSelectButton, { GroupModel, ResidentGroupTargetModel, SendMessageGroupTargetModel } from "@/pc/containers/safetyConfirms/TargetSelectButton.vue";
import GroupSelectModal from "@/pc/containers/safetyConfirms/GroupSelectModal.vue";
import InputTargetTypeForFamily from "@/pc/containers/safetyConfirms/InputTargetTypeForFamily.vue";

/**
 * ### 安否確認(家族)送信タブコンポーネント
 */
@Component({
  components: {
    PageLoading,
    ValidationAlert,
    RelativeLoading,
    InputTargetTypeForFamily,
    TargetSelectButton,
    FormTextarea,
    FormLabel,
    GroupSelectModal
  },
  $_veeValidate: { validator: "new" }
})
export default class SendFamilySafetyConfirmTab extends Vue {
  /** 安否確認(家族)テンプレートサービス */
  @Inject("FamilySafetyConfirmsTemplatesService") familySafetyConfirmsTemplatesService!: FamilySafetyConfirmsTemplatesService;
  /** 安否確認(家族)で送信する質問内容のマスタサービス */
  @Inject("FamilySafetyConfirmMastersService") familySafetyConfirmMastersService!: FamilySafetyConfirmMastersService;
  /** 宛先グループサービス */
  @Inject("SendMessageGroupsService") sendMessageGroupsService!: SendMessageGroupsService;
  /** 宛先グループメンバーサービス */
  @Inject("SendMessageGroupMembersService") sendMessageGroupMembersService!: SendMessageGroupMembersService;
  /** 家族サービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** 安否確認サービス */
  @Inject("SafetyConfirmsService") safetyConfirmsService!: SafetyConfirmsService;

  /** ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "読込み中...";

  isPageBusy: boolean = false;
  pageLoadingMessage: string = "読込み中...";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 災害ID */
  safetyConfirmsId: number = 0;
  /** 安否確認 */
  safetyConfirm: SafetyConfirmsGetModel | null = null;
  /** 安否確認(家族)で送信する質問内容のマスタ */
  masters: Array<FamilySafetyConfirmMastersGetModel> = [];
  /** 安否確認(家族)テンプレート */
  template: FamilySafetyConfirmsTemplatesGetModel | null = null;

  /** 宛先種別 */
  targetType: "all" | "group" | "" = "";
  /** 宛先種別の活性／非活性 */
  enableTarget: boolean = true;
  /** 宛先種別がグループの場合の宛先候補 */
  groups: GroupModel = {
    familyGroups: [],
    otherUserGroups: [],
    residentGroups: [],
    otherUserGroupMemberGroups: []
  };
  /** グループ種別 */
  groupType: GroupType = GroupType.UNKNOWN;
  /** 冒頭文 */
  openingSentence: string = "";
  /** 利用者個別状況リスト */
  residentStatuses: string = "";
  /** 施設全体状況リスト */
  facilityStatuses: string = "";

  /** 全家族 */
  allFamily: Array<FamilyGetModel> = [];
  /** 全家族をMAP化したもの key：利用者グループid、value：家族の配列 */
  familyMapByResidentGroupId: Map<number, Array<FamilyGetModel>> = new Map();

  get _targetTypeLabel(): string {
    if (!this.safetyConfirm) {
      return "";
    }

    const targets: string[] = [];
    if (this.safetyConfirm.latestLineMessage && this.safetyConfirm.latestLineMessage.receiverDetails) {
      for (const receiverDetail of this.safetyConfirm.latestLineMessage.receiverDetails) {
        const _receiverDetail: { groupName: string; members: string[] } = receiverDetail as any;
        targets.push(_receiverDetail.groupName);
      }
    }
    if (this.safetyConfirm.latestMailMessage && this.safetyConfirm.latestMailMessage.receiverDetails) {
      for (const receiverDetail of this.safetyConfirm.latestMailMessage.receiverDetails) {
        const _receiverDetail: { groupName: string; members: string[] } = receiverDetail as any;
        targets.push(_receiverDetail.groupName);
      }
    }

    return this.$stringUtil.removeDuplicatesUsingSet(targets).join("、"); // 重複を削除して、「、」で結合する
  }

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.createView();

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 施設状況を入力する項目のために安否確認(家族)テンプレートを取得する
   * - 施設状況の質問項目の情報のために安否確認(家族)で送信する質問内容のマスタサービスを取得する
   * - 前回入力内容が存在するかもしれないので、安否確認(家族)施設状況を取得する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 宛先選択肢のグループ情報を作成する
    const allFamily: Array<FamilyGetModel> = await this.getNonmaskedFamilies();
    this.allFamily = allFamily;
    const sendMessageGroups: Array<SendMessageGroupsGetModel> = await this.getSendMessageGroups();
    const residentGroups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const familyMapByResidentGroupId: Map<number, Array<FamilyGetModel>> = this.createFamilyMap(allFamily);
    this.groups = await this.createTargetGroups(sendMessageGroups, residentGroups, familyMapByResidentGroupId);
    this.familyMapByResidentGroupId = familyMapByResidentGroupId;

    const safetyConfirmsId: number = Number(this.$route.params.id);
    this.safetyConfirmsId = safetyConfirmsId;
    const template: FamilySafetyConfirmsTemplatesGetModel = await this.getTemplate();
    this.template = template;

    // 過去に送信していた場合、宛先を固定する
    // メッセージの内容は前回送信時のものを使用する
    const safetyConfirm: SafetyConfirmsGetModel = await this.getSafetyConfirm(safetyConfirmsId);
    this.safetyConfirm = safetyConfirm;

    if (safetyConfirm.latestLineMessage || safetyConfirm.latestMailMessage) {
      this.enableTarget = false;

      if (safetyConfirm.latestLineMessage) {
        this.openingSentence = safetyConfirm.latestLineMessage.functionContents.openingSentence;
      } else if (safetyConfirm.latestMailMessage) {
        this.openingSentence = safetyConfirm.latestMailMessage.functionContents.openingSentence;
      }
    }
    // 送信していない場合は事前設定の内容を入力項目に反映させる
    else {
      this.enableTarget = true;
      this.targetType = template.targetType ? template.targetType : "";

      // 宛先情報の復元は分岐が必要
      // v3.33.0以前のバージョンは宛先グループのgroupId（単体）を保持している
      // v3.34.0以降のバージョンは宛先情報（複数）を保持している
      // 宛先情報（複数）が存在する場合はv3.34.0以降のデータと判断し、存在せずgroupIdが存在する場合はv3.33.0以前のデータと判断する
      if (template.targets) {
        this.restoreTargetsByLatest(template.targets, this.groups);
      } else if (template.groupId != null) {
        this.restoreTargetsByOld(template, this.groups);
      }
      this.openingSentence = template.openingSentence;
    }

    const masters: Array<FamilySafetyConfirmMastersGetModel> = await this.getFamilySafetyConfirmMasters();
    this.masters = masters;

    const mastersMap: Map<number, FamilySafetyConfirmMastersGetModel> = this.createMasterMap(masters);
    const residentStatuses: string = this.createResidentStatuses(template, mastersMap);
    const facilityStatuses: string = this.createFacilityStatuses(template, mastersMap);
    this.residentStatuses = residentStatuses;
    this.facilityStatuses = facilityStatuses;

    this.isBusy = false;

    return;
  }

  /**
   * ### 宛先で選択候補とする家族を取得する
   */
  public async getNonmaskedFamilies(): Promise<Array<FamilyGetModel>> {
    let response: Array<FamilyGetModel> = [];

    try {
      const params: any = {
        masking: "none"
      };
      response = await this.familiesService.getsInFacility(userStore, params);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.familiesService.getCountInFacility(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300,
            masking: "none"
          };
          response = response.concat(await this.familiesService.getsInFacility(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 宛先グループを取得する
   */
  public async getSendMessageGroups(): Promise<Array<SendMessageGroupsGetModel>> {
    const response: Array<SendMessageGroupsGetModel> = await this.sendMessageGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 家族MAPを作成する
   *
   * - key：利用者グループid、value：家族の配列
   */
  private createFamilyMap(allFamily: Array<FamilyGetModel>): Map<number, Array<FamilyGetModel>> {
    const map: Map<number, Array<FamilyGetModel>> = new Map();
    for (const family of allFamily) {
      const families: Array<FamilyGetModel> | undefined = map.get(family.groupId);
      if (families) {
        families.push(family);
        map.set(family.groupId, families);
      } else {
        map.set(family.groupId, [family]);
      }
    }
    return map;
  }

  /**
   * ### 宛先のグループ情報を作成する
   */
  private async createTargetGroups(
    sendMessageGroups: Array<SendMessageGroupsGetModel>,
    residentGroups: Array<ResidentGroupsGetModel>,
    familyMapByResidentGroupId: Map<number, Array<FamilyGetModel>>
  ): Promise<GroupModel> {
    // 宛先グループの情報作成
    const targetFamilyGroups: Array<SendMessageGroupTargetModel> = [];
    for (const group of sendMessageGroups) {
      const _group: SendMessageGroupTargetModel = Object.assign({}, group) as SendMessageGroupTargetModel;
      _group.isTarget = false;
      _group.memberCount = group.members.length;
      if (_group.type == GroupType.FAMILY) {
        targetFamilyGroups.push({
          ..._group,
          show: true
        });
      }
    }
    // 利用者グループの情報を作成
    const targetResidentGroups: Array<ResidentGroupTargetModel> = [];
    for (const group of residentGroups) {
      const _group: ResidentGroupTargetModel = Object.assign({}, group) as ResidentGroupTargetModel;
      _group.isTarget = false;
      const families: Array<FamilyGetModel> | undefined = familyMapByResidentGroupId.get(_group.id);
      _group.memberCount = families ? families.length : 0;
      targetResidentGroups.push({
        ..._group,
        key: `residentGroup_${_group.id}`,
        show: true
      });
    }

    return {
      familyGroups: targetFamilyGroups,
      otherUserGroupMemberGroups: [],
      residentGroups: targetResidentGroups,
      otherUserGroups: []
    };
  }

  /**
   * ### 安否確認を取得する
   */
  public async getSafetyConfirm(safetyConfirmsId: number): Promise<SafetyConfirmsGetModel> {
    const response: SafetyConfirmsGetModel = await this.safetyConfirmsService.get(userStore, safetyConfirmsId);
    return response;
  }

  /**
   * ### 安否確認(家族)テンプレートを取得する
   *
   * - 現時点ではテンプレートは1件のみなのでid指定は1固定
   */
  public async getTemplate(): Promise<FamilySafetyConfirmsTemplatesGetModel> {
    const response: FamilySafetyConfirmsTemplatesGetModel = await this.familySafetyConfirmsTemplatesService.get(userStore, 1);
    return response;
  }

  /**
   * ### 宛先を復元する（v3.33.0以前）
   *
   * - 宛先グループIDで検索して一致するものを宛先対象とする
   * - v3.33.0以前は宛先グループ（家族）しか宛先に設定できなかったの検索対象はfamilyGroupsのみ
   */
  private restoreTargetsByOld(template: FamilySafetyConfirmsTemplatesGetModel, groups: GroupModel): void {
    if (template.groupId == 0) {
      this.targetType = "all";
    }
    for (const group of groups.familyGroups) {
      if (group.id == template.groupId) {
        group.isTarget = true;
        this.targetType = "group";
        return;
      }
    }
    return;
  }

  /**
   * ### 宛先を復元する（v3.34.0以降）
   *
   * - ソートキーリストで検索して一致するものを宛先対象とする
   * - 宛先グループ（家族）、利用者グループのみが対象となる
   */
  private restoreTargetsByLatest(targets: string[], groups: GroupModel): void {
    // 検索用にmapにする
    const map: Map<string, string> = new Map<string, string>();
    for (const target of targets) {
      map.set(target, target);
    }

    for (const group of groups.familyGroups) {
      if (map.has(String(group.id))) {
        group.isTarget = true;
        this.targetType = "group";
      }
    }
    for (const group of groups.residentGroups) {
      if (map.has(group.key)) {
        group.isTarget = true;
        this.targetType = "group";
      }
    }
    return;
  }

  /**
   * ### 安否確認(家族)で送信する質問内容のマスタを取得する
   */
  public async getFamilySafetyConfirmMasters(): Promise<Array<FamilySafetyConfirmMastersGetModel>> {
    const response: Array<FamilySafetyConfirmMastersGetModel> = await this.familySafetyConfirmMastersService.get(userStore);
    return response;
  }

  /**
   * ### 質問内容マスタのMAPを作成する
   */
  public createMasterMap(masters: Array<FamilySafetyConfirmMastersGetModel>): Map<number, FamilySafetyConfirmMastersGetModel> {
    const map: Map<number, FamilySafetyConfirmMastersGetModel> = new Map<number, FamilySafetyConfirmMastersGetModel>();
    for (const master of masters) {
      map.set(master.id, master);
    }
    return map;
  }

  /**
   * ### 使用する質問リストを作成する
   *
   * - テンプレート情報から質問を使用するかしないかを設定する
   */
  public createResidentStatuses(template: FamilySafetyConfirmsTemplatesGetModel, map: Map<number, FamilySafetyConfirmMastersGetModel>): string {
    const statuses: string[] = [];
    for (const id of template.useIds) {
      const status: FamilySafetyConfirmMastersGetModel | undefined = map.get(id);
      if (status != undefined && status.type == FamilySafetyConfirmMasterType.RESIDENT) {
        statuses.push(status.label);
      }
    }
    return statuses.join("、");
  }

  /**
   * ### 使用する質問リストを作成する
   *
   * - テンプレート情報から質問を使用するかしないかを設定する
   */
  public createFacilityStatuses(template: FamilySafetyConfirmsTemplatesGetModel, map: Map<number, FamilySafetyConfirmMastersGetModel>): string {
    const statuses: string[] = [];
    for (const id of template.useIds) {
      const status: FamilySafetyConfirmMastersGetModel | undefined = map.get(id);
      if (status != undefined && status.type == FamilySafetyConfirmMasterType.FACILITY) {
        statuses.push(status.label);
      }
    }
    return statuses.join("、");
  }

  /**
   * ### 宛先モーダルを表示する
   */
  onTargetBtn(): void {
    // メモリ渡しだと値変更が共有されてしまう
    // 値変更後に「キャンセル」された場合に困るので、コピーしたものを渡す
    const copy: GroupModel = JSON.parse(JSON.stringify(this.groups));
    const modal: any = this.$refs.groupSelectModal;
    modal.show(copy);
    return;
  }

  /**
   * ### 宛先モーダルの情報を更新する
   */
  addSendGroupTarget(targets: GroupModel): void {
    this.groups = targets;
    return;
  }

  /**
   * ### 送信ボタン押下イベント
   *
   * - 1回目の送信の場合
   *   - 入力要素のバリデーションを実施する
   *   - 親コンポーネントでメッセージ送信をするための情報を作成する
   *     - 宛先が「家族全員」かその他かでリクエストボディの作り方が分岐する
   *     - 送信先のLINEIDが空の場合はバリデーションエラーとして扱う
   *     - (家族及び宛先グループメンバーを取得しないとチェックが出来ないので、入力要素のバリデーション後に行っている)
   *   - 親コンポーネントに値を渡す
   * - 2回目の送信の場合
   *   - 前回と同じ宛先に再送信する
   */
  public async onSendMessage(safetyConfirmsId: number): Promise<void> {
    // 1回目の送信の場合
    if (this.safetyConfirm && !this.safetyConfirm.latestLineMessage && !this.safetyConfirm.latestMailMessage) {
      const validationResult: boolean = await this.$validator.validateAll();
      const validationTargets: boolean = this.validateTargets();

      this.isPageBusy = true;
      this.pageLoadingMessage = "読込み中...";

      if (validationResult && validationTargets) {
        this.showValidationAlert = false;

        let requestBodyForLine: FamilySendSafetyConfirmLineMessagesPostModel | null = null;
        let requestBodyForMail: FamilySendSafetyConfirmMailMessagesPostModel | null = null;

        switch (this.targetType) {
          // 家族全員
          case "all": {
            requestBodyForLine = this.createLineRequestBodyForAllFamily(this.allFamily);
            requestBodyForMail = this.createMailRequestBodyForAllFamily(this.allFamily);

            break;
          }
          // グループ
          case "group": {
            requestBodyForLine = await this.createLineRequestBodyForGroup(this.groups, this.familyMapByResidentGroupId);
            requestBodyForMail = await this.createMailRequestBodyForGroup(this.groups, this.familyMapByResidentGroupId);

            break;
          }
        }
        // バリデーションチェック
        if (requestBodyForLine && requestBodyForLine.targets.length < 1 && requestBodyForMail && requestBodyForMail.targets.length < 1) {
          this.$validator.errors.add({ field: "send-message-group-id", msg: "送信対象の家族がいないため、メッセージを送ることができません" });
        } else {
          if (requestBodyForLine && requestBodyForLine.targets.length < 1) {
            requestBodyForLine = null;
          }
          if (requestBodyForMail && requestBodyForMail.targets.length < 1) {
            requestBodyForMail = null;
          }
        }

        const model: SendFamilySafetyConfirmTabOnSendMessageModel = {
          safetyConfirmsId: safetyConfirmsId,
          requestBodyForLine: requestBodyForLine,
          requestBodyForMail: requestBodyForMail
        };
        this.$emit("onSendMessage", model);
      } else {
        this.showValidationAlert = true;
      }

      this.isPageBusy = false;
    }
    // 2回目以降の送信の場合、前回の宛先に送信する
    else if (this.safetyConfirm && (this.safetyConfirm.latestLineMessage || this.safetyConfirm.latestMailMessage)) {
      let requestBodyForLine: FamilySendSafetyConfirmLineMessagesPostModel | null = null;
      let requestBodyForMail: FamilySendSafetyConfirmMailMessagesPostModel | null = null;

      if (this.safetyConfirm.latestLineMessage && this.safetyConfirm.latestLineMessage.receiverDetails) {
        const targets: Array<SafetyConfirmTarget> = [];

        for (const receiverDetail of this.safetyConfirm.latestLineMessage.receiverDetails) {
          const _receiverDetail: { groupName: string; members: string[]; sortKey: string } = receiverDetail as any;
          targets.push({
            groupName: _receiverDetail.groupName,
            sortKeys: _receiverDetail.members
          });
        }

        requestBodyForLine = {
          targets,
          message: this.openingSentence,
          useIds: this.template ? this.template.useIds : []
        };
      }

      if (this.safetyConfirm.latestMailMessage && this.safetyConfirm.latestMailMessage.receiverDetails) {
        const targets: Array<SafetyConfirmTarget> = [];

        for (const receiverDetail of this.safetyConfirm.latestMailMessage.receiverDetails) {
          const _receiverDetail: { groupName: string; members: string[]; sortKey: string } = receiverDetail as any;
          targets.push({
            groupName: _receiverDetail.groupName,
            sortKeys: _receiverDetail.members
          });
        }

        requestBodyForMail = {
          targets,
          message: this.openingSentence
        };
      }

      const model: SendFamilySafetyConfirmTabOnSendMessageModel = {
        safetyConfirmsId: safetyConfirmsId,
        requestBodyForLine: requestBodyForLine,
        requestBodyForMail: requestBodyForMail
      };
      this.$emit("onSendMessage", model);
    }

    return;
  }

  private validateTargets(): boolean {
    this.$validator.errors.remove("target-groups");
    for (const group of this.groups.familyGroups) {
      if (group.isTarget) {
        return true;
      }
    }
    for (const group of this.groups.residentGroups) {
      if (group.isTarget) {
        return true;
      }
    }
    this.$validator.errors.add({ field: "target-groups", msg: "宛先は必須です" });
    return false;
  }

  /**
   * ### 宛先が家族全員の安否確認登録リクエストボディ(LINE)作成
   */
  public createLineRequestBodyForAllFamily(families: Array<FamilyGetModel>): FamilySendSafetyConfirmLineMessagesPostModel {
    const sortKeys: Array<string> = [];
    for (let family of families) {
      if (family.senderType == SenderType.LINE && family.familyLineId) {
        sortKeys.push(family.residentsSortKey);
      }
    }
    const requestBody: FamilySendSafetyConfirmLineMessagesPostModel = {
      targets: [
        {
          sortKeys: sortKeys,
          groupName: "家族全員"
        }
      ],
      message: this.openingSentence,
      useIds: this.template ? this.template.useIds : []
    };
    return requestBody;
  }

  /**
   * ### 宛先が家族全員の安否確認登録リクエストボディ(メール)作成
   */
  public createMailRequestBodyForAllFamily(families: Array<FamilyGetModel>): FamilySendSafetyConfirmMailMessagesPostModel {
    const sortKeys: Array<string> = [];
    for (let family of families) {
      if (family.senderType == SenderType.MAIL && family.familyMailaddress) {
        sortKeys.push(family.residentsSortKey);
      }
    }
    const requestBody: FamilySendSafetyConfirmMailMessagesPostModel = {
      targets: [
        {
          sortKeys: sortKeys,
          groupName: "家族全員"
        }
      ],
      message: this.openingSentence
    };
    return requestBody;
  }

  /**
   * ### 宛先が宛先グループの安否確認登録リクエストボディ(LINE)作成
   */
  public async createLineRequestBodyForGroup(
    groups: GroupModel,
    familyMapByResidentGroupId: Map<number, Array<FamilyGetModel>>
  ): Promise<FamilySendSafetyConfirmLineMessagesPostModel> {
    const targets: Array<SafetyConfirmTarget> = [];

    // 宛先グループ（家族）のメンバーのLINEユーザーで宛先を作成する
    for (const group of groups.familyGroups) {
      if (group.isTarget) {
        const sortKeys: Array<string> = [];
        const families: Array<SendMessageGroupMembersFamilyGetModel> = await this.getSendMessageGroupFamilies(group.id as number);

        for (const family of families) {
          if (family.senderType == SenderType.LINE && family.familyLineId) {
            sortKeys.push(family.residentsSortKey);
          }
        }

        if (0 < sortKeys.length) {
          targets.push({
            groupName: group.name,
            sortKeys
          });
        }
      }
    }
    // 利用者グループのメンバーのLINEユーザーで宛先を作成する
    for (const group of groups.residentGroups) {
      if (group.isTarget) {
        if (familyMapByResidentGroupId.has(group.id)) {
          const sortKeys: Array<string> = [];

          for (const family of familyMapByResidentGroupId.get(group.id) as Array<FamilyGetModel>) {
            if (family.senderType == SenderType.LINE && family.familyLineId) {
              sortKeys.push(family.residentsSortKey);
            }
          }

          if (0 < sortKeys.length) {
            targets.push({
              groupName: group.name,
              sortKeys
            });
          }
        }
      }
    }

    const requestBody: FamilySendSafetyConfirmLineMessagesPostModel = {
      targets: targets,
      message: this.openingSentence,
      useIds: this.template ? this.template.useIds : []
    };

    return requestBody;
  }

  /**
   * ### 宛先グループメンバーを取得する
   */
  public async getSendMessageGroupFamilies(groupId: number): Promise<Array<SendMessageGroupMembersFamilyGetModel>> {
    const members: Array<SendMessageGroupMembersFamilyGetModel> = (await this.sendMessageGroupMembersService.get(
      userStore,
      groupId
    )) as Array<SendMessageGroupMembersFamilyGetModel>;
    return members;
  }

  /**
   * ### 宛先が宛先グループの安否確認登録リクエストボディ(メール)作成
   */
  public async createMailRequestBodyForGroup(
    groups: GroupModel,
    familyMapByResidentGroupId: Map<number, Array<FamilyGetModel>>
  ): Promise<FamilySendSafetyConfirmMailMessagesPostModel> {
    const targets: Array<SafetyConfirmTarget> = [];

    // 宛先グループ（家族）のメンバーのメールユーザーで宛先を作成する
    for (const group of groups.familyGroups) {
      if (group.isTarget) {
        const sortKeys: Array<string> = [];
        const families: Array<SendMessageGroupMembersFamilyGetModel> = await this.getSendMessageGroupFamilies(group.id as number);

        for (const family of families) {
          if (family.senderType == SenderType.MAIL && family.familyMailaddress) {
            sortKeys.push(family.residentsSortKey);
          }
        }

        if (0 < sortKeys.length) {
          targets.push({
            groupName: group.name,
            sortKeys
          });
        }
      }
    }
    // 利用者グループのメンバーのメールユーザーで宛先を作成する
    for (const group of groups.residentGroups) {
      if (group.isTarget) {
        if (familyMapByResidentGroupId.has(group.id)) {
          const sortKeys: Array<string> = [];

          for (const family of familyMapByResidentGroupId.get(group.id) as Array<FamilyGetModel>) {
            if (family.senderType == SenderType.MAIL && family.familyMailaddress) {
              sortKeys.push(family.residentsSortKey);
            }
          }

          if (0 < sortKeys.length) {
            targets.push({
              groupName: group.name,
              sortKeys
            });
          }
        }
      }
    }

    const requestBody: FamilySendSafetyConfirmMailMessagesPostModel = {
      targets: targets,
      message: this.openingSentence
    };

    return requestBody;
  }

  /**
   * ### 宛先グループを取得する
   */
  public async getSendMessageGroup(groupId: number): Promise<SendMessageGroupsGetModel> {
    const response: SendMessageGroupsGetModel = await this.sendMessageGroupsService.get(userStore, groupId);
    return response;
  }

  /**
   * ### 宛先グループメンバーを取得する
   */
  public async getSendMessageGroupFamilyUserMembers(groupId: number): Promise<Array<SendMessageGroupMembersFamilyGetModel>> {
    const members: Array<SendMessageGroupMembersFamilyGetModel> = (await this.sendMessageGroupMembersService.get(
      userStore,
      groupId
    )) as Array<SendMessageGroupMembersFamilyGetModel>;
    return members;
  }

  /**
   * ### キャンセルボタン押下イベント
   */
  public onCancel(): void {
    this.$emit("onCancel");
    return;
  }
}

export interface SendFamilySafetyConfirmTabOnSendMessageModel {
  /** 災害ID */
  safetyConfirmsId: number;
  /** LINE送信情報 */
  requestBodyForLine: FamilySendSafetyConfirmLineMessagesPostModel | null;
  /** メール送信情報 */
  requestBodyForMail: FamilySendSafetyConfirmMailMessagesPostModel | null;
}
</script>

<style lang="scss" scoped>
#send-family-safety-confirm-tab {
  position: relative;

  .sned-message-btn,
  .cancel-btn {
    width: 120px;
  }

  .box-footer {
    padding: 10px 0 0 0 !important;
  }
}
</style>
